/**
 * trust_info.trustees may be stored as member UUID strings or as { id, relationship_to_grantor }.
 */

export function isRevocableTrust(accountType, defaultValue = false) {
  const typeLower = String(accountType || "").toLowerCase();
  if (typeLower.includes("irrevocable")) return false;
  if (typeLower.includes("revocable")) return true;
  return defaultValue;
}

export function getTrusteeMemberId(trustee) {
  if (trustee == null || trustee === "") return null;
  if (typeof trustee === "string") {
    const s = trustee.trim();
    return s || null;
  }
  if (typeof trustee === "object" && trustee.id != null && trustee.id !== "") {
    return String(trustee.id);
  }
  if (typeof trustee === "object" && trustee.household_member_id != null) {
    return String(trustee.household_member_id);
  }
  return null;
}

/** Normalize to { id, ...rest } for merges and payloads that expect objects. */
export function trusteeEntryToObject(trustee) {
  const id = getTrusteeMemberId(trustee);
  if (!id) {
    return typeof trustee === "object" && trustee && !Array.isArray(trustee)
      ? { ...trustee }
      : {};
  }
  if (typeof trustee === "object" && trustee && !Array.isArray(trustee)) {
    return { ...trustee, id };
  }
  return { id };
}

/** True when relationship to grantor is required (trustee is a different person than grantor). */
export function trusteeNeedsRelationshipToGrantor(trusteeMemberId, grantorMemberId) {
  if (!trusteeMemberId) return false;
  if (!grantorMemberId) return true;
  return String(trusteeMemberId) !== String(grantorMemberId);
}

const PLACEHOLDER_REL = new Set([
  "",
  "n/a",
  "na",
  "n.a.",
  "none",
  "-",
  "—",
]);

/** Blank / N/A-style values need a real relationship when required. */
export function isPlaceholderTrusteeRelationship(v) {
  if (v == null) return true;
  const s = String(v).trim().toLowerCase();
  return PLACEHOLDER_REL.has(s);
}

/** True if two or more trustee entries resolve to the same household member id. */
export function hasDuplicateTrusteeMemberIds(trustees) {
  const ids = (Array.isArray(trustees) ? trustees : [])
    .map((x) => getTrusteeMemberId(x))
    .filter(Boolean)
    .map(String);
  return ids.length !== new Set(ids).size;
}

/**
 * Drop empty rows, duplicate member ids, and clear relationship when trustee is the grantor.
 */
export function finalizeTrusteesForPayload(trustees, grantorMemberId) {
  const arr = Array.isArray(trustees) ? trustees : [];
  const seen = new Set();
  const out = [];
  for (const raw of arr) {
    const id = getTrusteeMemberId(raw);
    if (!id) continue;
    const sid = String(id);
    if (seen.has(sid)) continue;
    seen.add(sid);
    const base = trusteeEntryToObject(raw);
    const rel =
      grantorMemberId && sid === String(grantorMemberId)
        ? null
        : base.relationship_to_grantor != null && String(base.relationship_to_grantor).trim() !== ""
          ? String(base.relationship_to_grantor).trim()
          : null;
    out.push({ ...base, id, relationship_to_grantor: rel });
  }
  return out;
}

/**
 * Read-precedence for grantors:
 *   - grantors[]    → new shape, array of { id }
 *   - grantor_or_settler → legacy single id string
 * Returns array of member-id strings (may be empty).
 */
export function getTrustGrantorIds(trustInfo) {
  if (!trustInfo || typeof trustInfo !== "object") return [];
  const arr = Array.isArray(trustInfo.grantors) ? trustInfo.grantors : [];
  const fromArr = arr
    .map((g) => (g && g.id != null ? String(g.id).trim() : ""))
    .filter(Boolean);
  if (fromArr.length > 0) return fromArr;
  const legacy = trustInfo.grantor_or_settler;
  if (legacy && String(legacy).trim()) return [String(legacy).trim()];
  return [];
}

/**
 * Build a full trust_info dict ready to PUT.
 *   - existing: current trust_info from the server (any shape)
 *   - formChanges: { grantorIds: string[], patch?: object }
 *
 * Always writes grantors: [{id}, ...] from grantorIds (deduped, filtered).
 * NEVER writes grantor_or_settler — the legacy key is intentionally dropped.
 * Spreads existing first, then patch, then enforces grantors last so it wins.
 */
export function buildTrustInfoUpdatePayload(existing, formChanges) {
  const base = existing && typeof existing === "object" ? { ...existing } : {};
  delete base.grantor_or_settler;
  const patch =
    formChanges && formChanges.patch && typeof formChanges.patch === "object"
      ? formChanges.patch
      : {};
  const ids = Array.isArray(formChanges?.grantorIds) ? formChanges.grantorIds : [];
  const seen = new Set();
  const grantors = [];
  for (const raw of ids) {
    const id = raw == null ? "" : String(raw).trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    grantors.push({ id });
  }
  const payload = { ...base, ...patch, grantors };
  delete payload.revocable;
  return payload;
}
